import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Tabs } from 'antd';
import ActivityTab from './ActivityTab';
import OutboxTab from './OutboxTab';
import DecisionsTab from './DecisionsTab';
import PreviewTab from './PreviewTab';

const RESOURCE_PANELS = {
  activity: ActivityTab,
  outbox: OutboxTab,
  decisions: DecisionsTab,
  preview: PreviewTab,
};

/**
 * PA Agent admin module — sub-tabs driven by `admin_modules[].tabs` from partner detail.
 */
export default function PaAgentModule({
  partnerId,
  module: moduleDef,
  initialSubtab = 'activity',
  initialDecisionsFilters = null,
}) {
  const visibleTabs = useMemo(
    () => (Array.isArray(moduleDef?.tabs) ? moduleDef.tabs : []).filter((t) => t.visible !== false),
    [moduleDef],
  );

  const defaultKey = visibleTabs[0]?.key || 'activity';
  const [activeKey, setActiveKey] = useState(() => {
    const match = visibleTabs.find((t) => t.key === initialSubtab || t.resource === initialSubtab);
    return match?.key || defaultKey;
  });
  const [decisionsFilters, setDecisionsFilters] = useState(initialDecisionsFilters);

  useEffect(() => {
    const match = visibleTabs.find((t) => t.key === initialSubtab || t.resource === initialSubtab);
    if (match) setActiveKey(match.key);
  }, [initialSubtab, visibleTabs]);

  useEffect(() => {
    if (initialDecisionsFilters) setDecisionsFilters(initialDecisionsFilters);
  }, [initialDecisionsFilters]);

  const handleJumpToDecisions = useCallback((filters) => {
    setDecisionsFilters({ ...filters, _seq: Date.now() });
    const decisionsTab = visibleTabs.find((t) => t.resource === 'decisions');
    if (decisionsTab) setActiveKey(decisionsTab.key);
  }, [visibleTabs]);

  const items = useMemo(() => {
    return visibleTabs.map((tabDef) => {
      const Panel = RESOURCE_PANELS[tabDef.resource];
      if (!Panel) {
        return {
          key: tabDef.key,
          label: tabDef.label,
          children: null,
        };
      }
      const isDecisions = tabDef.resource === 'decisions';
      const isOutbox = tabDef.resource === 'outbox';
      const isActivity = tabDef.resource === 'activity';
      return {
        key: tabDef.key,
        label: tabDef.label,
        children: (
          <Panel
            partnerId={partnerId}
            initialFilters={(isDecisions || isActivity) ? (decisionsFilters || undefined) : undefined}
            onJumpToDecisions={(isOutbox || isActivity) ? handleJumpToDecisions : undefined}
          />
        ),
      };
    });
  }, [visibleTabs, partnerId, decisionsFilters, handleJumpToDecisions]);

  if (!partnerId) return null;
  if (!items.length) return null;

  return (
    <Tabs activeKey={activeKey} onChange={setActiveKey} items={items} destroyInactiveTabPane={false} />
  );
}
